import React from 'react';
import type { PageSection } from './types';
import { Trash2 } from 'lucide-react';

interface CanvasProps {
  sections: PageSection[];
  onReorder: (dragIndex: number, hoverIndex: number) => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  selectedId: string | null;
}

const SectionPreview: React.FC<{ section: PageSection }> = ({ section }) => {
  const { section_type, section_data } = section;
  const style = {
    color: section_data.colors?.text_color,
    backgroundColor: section_data.colors?.bg_color,
    borderColor: section_data.colors?.border_color,
    backgroundImage: section_data.colors?.gradient_start && section_data.colors?.gradient_end 
      ? `linear-gradient(${section_data.colors.gradient_direction || 'to right'}, ${section_data.colors.gradient_start}, ${section_data.colors.gradient_end})`
      : undefined
  };

  switch (section_type) {
    case 'heading':
    case 'title':
    case 'feature_heading':
      const Tag = (section_data.level || 'h2') as keyof JSX.IntrinsicElements;
      return <Tag style={style} className="font-bold text-2xl p-2">{section_data.text || 'Heading Text'}</Tag>;
    
    case 'paragraph':
    case 'text_area':
      return <p style={style} className="p-2 whitespace-pre-wrap">{section_data.text || 'Paragraph text...'}</p>;

    case 'hero_image':
      return (
        <div className="relative h-48 bg-gray-800 flex items-center justify-center overflow-hidden rounded" style={style}>
          {section_data.url && <img src={section_data.url} className="absolute inset-0 w-full h-full object-cover opacity-50" />}
          <h2 className="relative z-10 text-3xl font-bold text-white">{section_data.text || 'Hero Heading'}</h2>
        </div>
      );

    case 'media':
      return (
        <div className="p-2" style={style}>
          {section_data.url ? (
             section_data.type === 'video' ? <video src={section_data.url} controls className="w-full max-h-64 rounded" /> : <img src={section_data.url} className="w-full max-h-64 object-cover rounded" />
          ) : <div className="h-32 bg-gray-700 flex items-center justify-center text-gray-400">No Media URL</div>}
        </div>
      );

    case 'tags':
      return (
        <div className="flex gap-2 flex-wrap p-2" style={style}>
          {(section_data.tags || ['Tag 1', 'Tag 2']).map((t, i) => (
            <span key={i} className="px-2 py-1 bg-cyan-900/50 text-cyan-300 rounded text-sm border border-cyan-500/20">{t}</span>
          ))}
        </div>
      );

    case 'contact_form':
      return (
        <div className="p-4 border border-white/10 rounded bg-white/5" style={style}>
          <p className="text-center text-gray-400 mb-2">Contact Form Placeholder</p>
          <div className="space-y-2 opacity-50 pointer-events-none">
            <input className="w-full bg-black/20 border border-white/10 p-2 rounded" placeholder="Name" />
            <input className="w-full bg-black/20 border border-white/10 p-2 rounded" placeholder="Email" />
            <textarea className="w-full bg-black/20 border border-white/10 p-2 rounded" placeholder="Message"></textarea>
            <button className="px-4 py-2 bg-cyan-600 rounded text-white">Send</button>
          </div>
        </div>
      );

    case 'button':
      return (
        <div className="p-2" style={style}>
          <div className={`flex ${section_data.align === 'center' ? 'justify-center' : section_data.align === 'right' ? 'justify-end' : 'justify-start'}`}>
            <button className="px-5 py-2 rounded bg-cyan-600 text-white font-semibold">
              {section_data.button_text || 'Button'}
            </button>
          </div>
        </div>
      );

    case 'divider':
      return (
        <div className="p-2" style={style}>
          <hr
            style={{
              border: 0,
              borderTop: `${section_data.divider_thickness || 1}px ${section_data.divider_style || 'solid'} ${section_data.colors?.border_color || 'rgba(255,255,255,0.25)'}`
            }}
          />
        </div>
      );

    case 'spacer':
      return (
        <div className="w-full bg-white/5 rounded" style={{ height: `${section_data.spacer_height || 48}px` }} />
      );

    case 'list':
      return (
        <ul className="p-2 list-disc list-inside space-y-1" style={style}>
          {(section_data.list_items || ['List item']).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );

    case 'quote':
      return (
        <blockquote className="p-4 border-l-4 border-cyan-500 bg-white/5 rounded-r" style={style}>
          <p className="text-lg italic">"{section_data.text || 'Quote'}"</p>
          {section_data.quote_author && <footer className="mt-2 text-sm text-gray-400">- {section_data.quote_author}</footer>}
        </blockquote>
      );

    case 'two_columns':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2" style={style}>
          {(section_data.columns || [{}, {}]).slice(0, 2).map((col, idx) => (
            <div key={idx} className="rounded border border-white/10 p-3 bg-white/5">
              {col.image_url && <img src={col.image_url} className="w-full h-36 object-cover rounded mb-2" />}
              <h4 className="font-semibold mb-1">{col.heading || `Column ${idx + 1}`}</h4>
              <p className="text-sm text-gray-300">{col.text || 'Column text...'}</p>
            </div>
          ))}
        </div>
      );

    default:
      return <div className="p-2 text-gray-500">Unknown Component: {section_type}</div>;
  }
};

export const Canvas: React.FC<CanvasProps> = ({ sections, onSelect, onDelete, selectedId, onReorder }) => {
  // Simple reordering implementation for now: Move Up/Down buttons in the toolbar of each item
  // Or just simple list rendering
  
  return (
    <div className="flex-1 bg-[#0f1016] p-8 overflow-y-auto h-full">
      <div className="max-w-4xl mx-auto space-y-4 min-h-[500px] border-2 border-dashed border-white/5 rounded-lg p-4">
        {sections.length === 0 && (
          <div className="h-full flex items-center justify-center text-gray-500">
            Select a component from the sidebar to add it here
          </div>
        )}
        
        {sections.map((section, index) => (
          <div 
            key={section.id}
            onClick={(e) => { e.stopPropagation(); onSelect(section.id); }}
            className={`relative group border rounded-lg transition-all ${selectedId === section.id ? 'border-cyan-500 ring-1 ring-cyan-500 bg-[#1a1a2e]' : 'border-white/5 bg-[#13141c] hover:border-white/20'}`}
          >
            <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button 
                onClick={(e) => { e.stopPropagation(); if(index > 0) onReorder(index, index - 1); }} 
                className="p-1.5 bg-gray-800 text-white rounded hover:bg-gray-700" title="Move Up"
              >
                ▲
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); if(index < sections.length - 1) onReorder(index, index + 1); }} 
                className="p-1.5 bg-gray-800 text-white rounded hover:bg-gray-700" title="Move Down"
              >
                ▼
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onDelete(section.id); }} 
                className="p-1.5 bg-red-900/80 text-white rounded hover:bg-red-700" title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
            
            <div className="p-4">
              <SectionPreview section={section} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
